/**
 * Tab-Leiste Komponente mit Drag & Drop Unterstützung
 * Tabs für Akten/Aufgaben sind Drop-Ziele für Dokumente aus dem Posteingang.
 * Beim Hover über gültige Tabs wird eine KI-Vorschau mit Athena-Empfehlung angezeigt.
 */

import { useTabs } from '@/contexts/TabContext';
import { X, Home, Inbox, Users, UserCircle, CheckSquare, Briefcase, Mail, Euro, BookOpen, FileText, Settings, Download, Shield, Send, Sparkles, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDrop } from 'react-dnd';
import { DND_TYPES } from '@/lib/dndTypes';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const ICON_MAP = {
  dashboard: Home,
  inbox: Inbox,
  users: Users,
  user: UserCircle,
  tasks: CheckSquare,
  briefcase: Briefcase,
  mail: Mail,
  euro: Euro,
  book: BookOpen,
  file: FileText,
  settings: Settings,
  download: Download,
  shield: Shield,
  send: Send,
};

// Farbcodierung für Tab-Typen
const TAB_TYPE_COLORS = {
  dashboard:      { active: 'bg-white border-slate-300', inactive: 'bg-slate-100 hover:bg-slate-200', dot: 'bg-slate-500' },
  akte:           { active: 'bg-blue-50 border-blue-300', inactive: 'bg-blue-50/50 hover:bg-blue-100/70', dot: 'bg-blue-500' },
  blick:          { active: 'bg-teal-50 border-teal-300', inactive: 'bg-teal-50/50 hover:bg-teal-100/70', dot: 'bg-teal-500' },
  uebergabe:      { active: 'bg-purple-50 border-purple-300', inactive: 'bg-purple-50/50 hover:bg-purple-100/70', dot: 'bg-purple-500' },
  vorgang:        { active: 'bg-amber-50 border-amber-300', inactive: 'bg-amber-50/50 hover:bg-amber-100/70', dot: 'bg-amber-500' },
  'akten-liste':  { active: 'bg-white border-slate-300', inactive: 'bg-slate-100 hover:bg-slate-200', dot: 'bg-blue-400' },
  aufgaben:       { active: 'bg-orange-50 border-orange-300', inactive: 'bg-orange-50/50 hover:bg-orange-100/70', dot: 'bg-orange-500' },
  finanzen:       { active: 'bg-emerald-50 border-emerald-300', inactive: 'bg-emerald-50/50 hover:bg-emerald-100/70', dot: 'bg-emerald-500' },
  kommunikation:  { active: 'bg-sky-50 border-sky-300', inactive: 'bg-sky-50/50 hover:bg-sky-100/70', dot: 'bg-sky-500' },
  posteingang:    { active: 'bg-indigo-50 border-indigo-300', inactive: 'bg-indigo-50/50 hover:bg-indigo-100/70', dot: 'bg-indigo-500' },
  aktenuebernahme:{ active: 'bg-violet-50 border-violet-300', inactive: 'bg-violet-50/50 hover:bg-violet-100/70', dot: 'bg-violet-500' },
  personen:       { active: 'bg-rose-50 border-rose-300', inactive: 'bg-rose-50/50 hover:bg-rose-100/70', dot: 'bg-rose-500' },
  admin:          { active: 'bg-slate-50 border-slate-400', inactive: 'bg-slate-100 hover:bg-slate-200', dot: 'bg-slate-600' },
  default:        { active: 'bg-white border-slate-300', inactive: 'bg-slate-100 hover:bg-slate-200', dot: 'bg-slate-400' },
};

/**
 * Prüft ob ein Tab als Drop-Ziel für Dokumente dient
 * Regex matches UUIDs with dashes
 */
function getDropTarget(tabPath) {
  const match = tabPath.match(/^\/akten\/([a-f0-9-]+)$/);
  if (match) return { type: 'akte', id: match[1] };

  const aufgabeMatch = tabPath.match(/^\/aufgaben\/([a-f0-9-]+)$/);
  if (aufgabeMatch) return { type: 'aufgabe', id: aufgabeMatch[1] };

  return null;
}

/**
 * Berechnet die KI-Konfidenz für eine Zuordnung
 * Vergleicht die Athena-Empfehlung mit dem Drop-Ziel
 */
function getKiConfidence(kiEmpfehlung, dropTarget) {
  if (!kiEmpfehlung || !dropTarget) return null;

  const ki = kiEmpfehlung.ki_empfehlung || {};
  const gefKlient = kiEmpfehlung.gefundener_klient || ki.klient;

  if (dropTarget.type === 'akte' && gefKlient?.id === dropTarget.id) {
    return { score: 95, label: 'Sehr hohe Übereinstimmung', color: 'emerald' };
  }
  if (dropTarget.type === 'akte' && gefKlient) {
    return { score: 30, label: 'Andere Akte empfohlen', color: 'amber' };
  }
  if (dropTarget.type === 'aufgabe' && ki.aufgabe?.id === dropTarget.id) {
    return { score: 90, label: 'Hohe Übereinstimmung', color: 'emerald' };
  }
  return null;
}

/** Einzelner Tab mit optionaler Drop-Funktionalität & KI-Tooltip */
function DroppableTab({ tab, isActive, onTabClick, onCloseTab }) {
  const Icon = ICON_MAP[tab.icon] || FileText;
  const dropTarget = getDropTarget(tab.path);
  const colors = TAB_TYPE_COLORS[tab.tabType] || TAB_TYPE_COLORS.default;

  const [{ isOver, canDrop, dragItem }, dropRef] = useDrop(() => ({
    accept: DND_TYPES.DOKUMENT,
    canDrop: () => !!dropTarget,
    drop: async (item) => {
      if (!dropTarget || !item?.dokument) return;
      const dok = item.dokument;
      try {
        if (dropTarget.type === 'akte') {
          await betreuungAPI.updateDokument(dok.id, {
            klient_id: dropTarget.id,
            dokumententyp: 'zugeordnet',
          });
          toast.success(`Dokument "${dok.dateiname}" → Akte: ${tab.title}`);
        } else if (dropTarget.type === 'aufgabe') {
          await betreuungAPI.updateDokument(dok.id, {
            dokumententyp: 'zugeordnet',
          });
          await betreuungAPI.createAufgabenVerlauf(dropTarget.id, {
            text: `Dokument "${dok.dateiname}" per Drag & Drop zugeordnet`,
            typ: 'dokument',
          });
          toast.success(`Dokument "${dok.dateiname}" → Aufgabe: ${tab.title}`);
        }
        window.dispatchEvent(new CustomEvent('dokument-zugeordnet'));
      } catch {
        toast.error('Fehler beim Zuordnen des Dokuments');
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      dragItem: monitor.getItem(),
    }),
  }), [dropTarget, tab]);

  const kiConfidence = isOver && canDrop && dragItem?.kiEmpfehlung
    ? getKiConfidence(dragItem.kiEmpfehlung, dropTarget)
    : null;

  const dropHighlight = canDrop && isOver
    ? 'ring-2 ring-blue-500 bg-blue-100 scale-105'
    : canDrop
      ? 'ring-1 ring-dashed ring-blue-300'
      : '';

  return (
    <div className="relative" ref={dropRef}>
      <div
        onClick={() => onTabClick(tab)}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-t-lg cursor-pointer
          transition-all group min-w-[120px] max-w-[240px]
          border-t border-l border-r
          ${isActive
            ? `${colors.active} text-slate-800 shadow-sm -mb-px`
            : `${colors.inactive} text-slate-600 border-transparent`
          }
          ${dropHighlight}
        `}
        data-testid={`tab-${tab.id}`}
      >
        <span className={`w-2 h-2 rounded-full shrink-0 ${colors.dot}`} />
        <Icon className="w-3.5 h-3.5 shrink-0 opacity-60" />
        <span className="text-sm truncate flex-1" title={tab.title}>{tab.title}</span>
        {canDrop && isOver && (
          <ArrowRight className="w-3 h-3 text-blue-600 shrink-0 animate-pulse" />
        )}
        {tab.closable && !isOver && (
          <button
            onClick={(e) => onCloseTab(e, tab.id)}
            className={`
              p-0.5 rounded hover:bg-slate-300/50
              ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
            `}
            data-testid={`tab-close-${tab.id}`}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* KI-Vorschau Tooltip beim Hover über gültiges Drop-Ziel */}
      {canDrop && isOver && dragItem?.dokument && (
        <div
          className="absolute top-full left-0 mt-1 z-[100] w-72 bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
          data-testid="drop-preview-tooltip"
        >
          <div className="px-3 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-800">
              {dropTarget.type === 'akte' ? 'Zur Akte zuordnen' : 'Zur Aufgabe zuordnen'}
            </span>
          </div>

          <div className="px-3 py-2">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="truncate">{dragItem.dokument.dateiname || 'Dokument'}</span>
            </div>
            <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
              <span>→</span>
              <span className="font-medium">{tab.title}</span>
            </div>
          </div>

          {/* KI-Konfidenz */}
          {kiConfidence ? (
            <div className={`px-3 py-2 border-t flex items-center gap-2 ${
              kiConfidence.color === 'emerald' ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'
            }`}>
              <Sparkles className={`w-4 h-4 shrink-0 ${
                kiConfidence.color === 'emerald' ? 'text-emerald-500' : 'text-amber-500'
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${
                    kiConfidence.color === 'emerald' ? 'text-emerald-700' : 'text-amber-700'
                  }`}>
                    Zeo: {kiConfidence.label}
                  </span>
                  <span className={`text-xs font-bold ${
                    kiConfidence.color === 'emerald' ? 'text-emerald-600' : 'text-amber-600'
                  }`}>
                    {kiConfidence.score}%
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-1 h-1 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      kiConfidence.color === 'emerald' ? 'bg-emerald-500' : 'bg-amber-500'
                    }`}
                    style={{ width: `${kiConfidence.score}%` }}
                  />
                </div>
              </div>
            </div>
          ) : dragItem.kiEmpfehlung ? (
            <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-500">Zeo: Keine spezifische Empfehlung</span>
            </div>
          ) : (
            <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50">
              <span className="text-[10px] text-slate-400">Loslassen zum Zuordnen</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TabBar() {
  const { tabs, activeTabId, activateTab, closeTab } = useTabs();
  const navigate = useNavigate();

  const handleTabClick = (tab) => {
    activateTab(tab.id);
    navigate(tab.path);
  };

  const handleCloseTab = (e, tabId) => {
    e.stopPropagation();
    const tabIndex = tabs.findIndex(t => t.id === tabId);
    
    closeTab(tabId);
    
    if (activeTabId === tabId) {
      const remainingTabs = tabs.filter(t => t.id !== tabId);
      const newActiveIndex = Math.max(0, tabIndex - 1);
      const newActiveTab = remainingTabs[newActiveIndex] || remainingTabs[0];
      if (newActiveTab) {
        navigate(newActiveTab.path);
      }
    }
  };

  return (
    <div className="bg-slate-100 border-b border-slate-200 px-2 py-1 flex items-center gap-1 overflow-x-auto" data-testid="tab-bar">
      {tabs.map((tab) => (
        <DroppableTab
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
          onTabClick={handleTabClick}
          onCloseTab={handleCloseTab}
        />
      ))}
      
      {tabs.length === 1 && (
        <div className="text-xs text-slate-400 ml-4 flex items-center gap-1">
          <span>Nutzen Sie den Chat-Assistenten um mehrere Tabs zu öffnen</span>
        </div>
      )}
      
      {tabs.length > 1 && (
        <div className="ml-auto text-xs text-slate-400 px-2">
          {tabs.length} Tabs offen
        </div>
      )}
    </div>
  );
}
